import { useEffect, useState } from 'react';

import { CalendarClock } from 'lucide-react';
import { toast } from 'sonner';

import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';

import { CartaoDeConfiguracao } from './CartaoDeConfiguracao';
import { CHAVES, useConfiguracao, type PrazoDeFechamento } from './dados';

/** O dia limite aceito vai de 1 a 28 — acima disso fevereiro quebraria. */
const DIA_MINIMO = 1;
const DIA_MAXIMO = 28;
const DIA_PADRAO = 25;

function limitar(valor: number): number {
  if (!Number.isFinite(valor)) {
    return DIA_PADRAO;
  }
  return Math.max(DIA_MINIMO, Math.min(DIA_MAXIMO, Math.trunc(valor)));
}

/**
 * `/admin/configuracoes/fechamento` — o dia do mês seguinte em que a
 * competência vence, usado como prazo legal pelo cockpit financeiro.
 *
 * Aceita valores distintos para pessoa física e para pessoa jurídica ou
 * cooperativa, porque o fluxo de pagamento das duas é diferente.
 *
 * "Salvar" não grava — ver `CartaoDeConfiguracao.tsx`.
 */
export function PrazoFechamentoPage() {
  const consulta = useConfiguracao<PrazoDeFechamento>(CHAVES.prazoFechamento);

  const [pessoaFisica, setPessoaFisica] = useState(DIA_PADRAO);
  const [pessoaJuridica, setPessoaJuridica] = useState(DIA_PADRAO);

  useEffect(() => {
    if (consulta.data) {
      setPessoaFisica(limitar(consulta.data.pf));
      setPessoaJuridica(limitar(consulta.data.pj));
    }
  }, [consulta.data]);

  function validar(): boolean {
    const forasDaFaixa = [pessoaFisica, pessoaJuridica].some(
      (dia) => !Number.isInteger(dia) || dia < DIA_MINIMO || dia > DIA_MAXIMO,
    );
    if (forasDaFaixa) {
      toast.error('Dia inválido', {
        description: `Informe um dia entre ${DIA_MINIMO} e ${DIA_MAXIMO} para as duas naturezas.`,
      });
      return false;
    }
    return true;
  }

  return (
    <CartaoDeConfiguracao
      titulo="Prazo de fechamento mensal"
      descricao="Define o dia do mês seguinte usado como prazo legal de cada competência."
      tituloDoCartao="Dia limite (mês seguinte)"
      icone={CalendarClock}
      carregando={consulta.isLoading}
      comErro={consulta.isError}
      mensagemDeErro="Não foi possível carregar o prazo de fechamento agora. Verifique sua conexão e tente novamente."
      aoTentarDeNovo={() => void consulta.refetch()}
      aoValidar={validar}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="prazo-pf">Pessoa física</Label>
          <Input
            id="prazo-pf"
            type="number"
            min={DIA_MINIMO}
            max={DIA_MAXIMO}
            value={pessoaFisica}
            onChange={(evento) => setPessoaFisica(limitar(Number(evento.target.value)))}
            className="w-32"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="prazo-pj">Pessoa jurídica e cooperativa</Label>
          <Input
            id="prazo-pj"
            type="number"
            min={DIA_MINIMO}
            max={DIA_MAXIMO}
            value={pessoaJuridica}
            onChange={(evento) => setPessoaJuridica(limitar(Number(evento.target.value)))}
            className="w-32"
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Com o dia {pessoaFisica}, por exemplo, a competência de março de 2026 vence em{' '}
        {String(pessoaFisica).padStart(2, '0')}/04/2026. O limite é {DIA_MAXIMO} para que o prazo
        exista em fevereiro.
      </p>
    </CartaoDeConfiguracao>
  );
}

export default PrazoFechamentoPage;
